// src/pages/offlineSales/terminal/components/CustomerSelectorModal.jsx
import React, { useState, useEffect } from 'react';
import { X, Search, Plus, User, Mail, MapPin, Home, FileText, ChevronDown, AlertCircle, Save } from 'lucide-react';
import { useLocalization } from '../../../../contexts/LocalizationContext';
import { useAlert } from '../../../../contexts/AlertContext';
import { getCustomers, createCustomer } from '../../../../services/api/offlineSales/customers';
import { getCities } from '../../../../services/api/orderReference';
import useCitySearch from '../../../../hooks/useCitySearch';
import PhoneNumberInput from '../../../../components/ui/PhoneNumberInput/PhoneNumberInput';
import styles from '../OfflineSalesTerminal.module.css';

const CustomerSelectorModal = ({ current, cashSalesCustomer, onSelect, onClose, initialCreate = false }) => {
  const { t } = useLocalization();
  const { error } = useAlert();
  const [query, setQuery] = useState('');
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(initialCreate);
  const [form, setForm] = useState({ name: '', phone: '', email: '', address: '', notes: '', cityId: null });
  const [creating, setCreating] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [cities, setCities] = useState([]);
  const [existingCustomer, setExistingCustomer] = useState(null);
  const [checkingPhone, setCheckingPhone] = useState(false);

  const {
    searchTerm: citySearch,
    filteredCities,
    selectedIndex: citySelectedIndex,
    showResults: showCityResults,
    handleSearchChange: handleCitySearchChange,
    handleKeyDown: handleCityKeyDown,
    selectCity,
    clearSearch: clearCitySearch,
    hideResults: hideCityResults,
    showResultsDropdown: showCityDropdown,
  } = useCitySearch(cities);

  useEffect(() => {
    getCities().then(data => setCities(Array.isArray(data) ? data : (data?.cities || []))).catch(() => {});
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => search(query), 300);
    return () => clearTimeout(timer);
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { search(''); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const search = async (q) => {
    try {
      setLoading(true);
      const result = await getCustomers({ search: q, limit: 20 });
      setCustomers(result.customers || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  // Look up the typed phone number so an existing customer can be reused instead of duplicated
  useEffect(() => {
    setExistingCustomer(null);
    const digits = (form.phone || '').replace(/\D/g, '');
    if (!showCreate || phoneError || digits.length < 9) return;
    const last9 = digits.slice(-9);
    const timer = setTimeout(async () => {
      try {
        setCheckingPhone(true);
        const result = await getCustomers({ search: last9, limit: 5 });
        const match = (result.customers || []).find(c =>
          (c.phone || '').replace(/\D/g, '').endsWith(last9)
        );
        setExistingCustomer(match || null);
      } catch {
        // silent — creating still works, the duplicate check is a convenience
      } finally {
        setCheckingPhone(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [form.phone, phoneError, showCreate]);

  const handleCitySelect = (city) => {
    selectCity(city);
    setForm(f => ({ ...f, cityId: city.id }));
  };

  const handleCityKeyDownWrapper = (e) => {
    const selected = handleCityKeyDown(e);
    if (selected) handleCitySelect(selected);
  };

  const handleClearCity = () => {
    clearCitySearch();
    setForm(f => ({ ...f, cityId: null }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || phoneError) return;
    try {
      setCreating(true);
      const result = await createCustomer(form);
      onSelect(result.customer);
    } catch (err) {
      error(err?.response?.data?.message || 'Failed to create customer');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>{showCreate ? 'Add New Customer' : t('pos.selectCustomer')}</h3>
          <button onClick={onClose} className={styles.modalClose}><X size={18} /></button>
        </div>

        {!showCreate ? (
          <>
            <div className={styles.searchRow}>
              <Search size={14} />
              <input
                autoFocus
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder={t('pos.searchCustomers')}
                className={styles.modalSearch}
              />
            </div>

            <div className={styles.customerList}>
              {/* Cash Sales option */}
              <button
                className={`${styles.customerRow} ${!current ? styles.selected : ''}`}
                onClick={() => onSelect(null)}
              >
                <span className={styles.customerName}>{t('pos.cashSales')}</span>
              </button>

              {loading ? (
                <div className={styles.listLoading}>...</div>
              ) : customers.length === 0 ? (
                <div className={styles.listEmpty}>{t('pos.noCustomersFound')}</div>
              ) : (
                customers.map(c => (
                  <button
                    key={c.id}
                    className={`${styles.customerRow} ${current?.id === c.id ? styles.selected : ''}`}
                    onClick={() => onSelect(c)}
                  >
                    <span className={styles.customerName}>{c.name}</span>
                    <span className={styles.customerPhone}>{c.phone || ''}</span>
                    {c.loyaltyPoints > 0 && (
                      <span className={styles.customerPoints}>{c.loyaltyPoints} pts</span>
                    )}
                  </button>
                ))
              )}
            </div>

            <div className={styles.modalFooter}>
              <button className={styles.actionBtnSecondary} onClick={() => setShowCreate(true)}>
                <Plus size={14} /> {t('pos.newCustomer')}
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={handleCreate} className={`${styles.createForm} ${styles.cfForm}`}>
            <div className={styles.cfField}>
              <label className={styles.cfLabel}>{t('pos.customerPhone')} *</label>
              <PhoneNumberInput
                value={form.phone}
                onChange={val => setForm(f => ({ ...f, phone: val }))}
                onValidationChange={(hasError, msg) => setPhoneError(hasError ? msg : '')}
                placeholder="Enter phone number"
                required
              />
            </div>

            {existingCustomer ? (
              <div className={styles.cfExisting}>
                <div className={styles.cfExistingInfo}>
                  <span className={styles.cfExistingTitle}>Customer already exists</span>
                  <span className={styles.cfExistingName}>{existingCustomer.name}</span>
                  <span className={styles.cfExistingPhone}>{existingCustomer.phone}</span>
                </div>
                <button type="button" className={styles.cfUseBtn} onClick={() => onSelect(existingCustomer)}>
                  Use this customer
                </button>
              </div>
            ) : (
              <div className={styles.cfNotice}>
                <AlertCircle size={16} />
                {checkingPhone
                  ? 'Checking phone number...'
                  : 'If this phone number already exists in the system, we will show the customer details.'}
              </div>
            )}

            <div className={styles.cfField}>
              <label className={styles.cfLabel}>{t('pos.customerName')} *</label>
              <div className={styles.cfInputWrap}>
                <User size={18} className={styles.cfIcon} />
                <input
                  required
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Enter customer name"
                  className={styles.cfInput}
                />
              </div>
            </div>

            <div className={styles.cfField}>
              <label className={styles.cfLabel}>{t('pos.customerEmail')}</label>
              <div className={styles.cfInputWrap}>
                <Mail size={18} className={styles.cfIcon} />
                <input
                  type="email"
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="Enter email address (optional)"
                  className={styles.cfInput}
                />
              </div>
            </div>

            <div className={styles.cfField}>
            <label className={styles.cfLabel}>{t('pos.customerCity')}</label>
            <div className={`${styles.citySearchWrapper} ${styles.cfInputWrap}`}>
              <MapPin size={18} className={styles.cfIcon} />
              <input
                type="text"
                value={citySearch}
                onChange={e => handleCitySearchChange(e.target.value)}
                onKeyDown={handleCityKeyDownWrapper}
                onFocus={showCityDropdown}
                onBlur={() => setTimeout(hideCityResults, 150)}
                placeholder="Search or select city..."
                className={styles.cfInput}
                autoComplete="off"
              />
              {citySearch ? (
                <button type="button" onClick={handleClearCity} className={styles.cfTrailingBtn}>
                  <X size={14} />
                </button>
              ) : (
                <ChevronDown size={18} className={styles.cfTrailingIcon} />
              )}
              {showCityResults && filteredCities.length > 0 && (
                <div className={styles.cityDropdown}>
                  {filteredCities.map((city, idx) => (
                    <div
                      key={city.id}
                      className={`${styles.cityOption} ${idx === citySelectedIndex ? styles.cityOptionSelected : ''}`}
                      onMouseDown={() => handleCitySelect(city)}
                    >
                      <div className={styles.cityOptionName}>{city.name}</div>
                      {city.District && (
                        <div className={styles.cityOptionSub}>{city.District.name}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            </div>

            <div className={styles.cfField}>
              <label className={styles.cfLabel}>{t('pos.customerAddress')}</label>
              <div className={`${styles.cfInputWrap} ${styles.cfTextareaWrap}`}>
                <Home size={18} className={styles.cfIcon} />
                <textarea
                  rows={2}
                  value={form.address}
                  onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                  placeholder="Enter address (optional)"
                  className={styles.cfInput}
                />
              </div>
            </div>

            <div className={styles.cfField}>
              <label className={styles.cfLabel}>{t('pos.customerNotes')}</label>
              <div className={`${styles.cfInputWrap} ${styles.cfTextareaWrap}`}>
                <FileText size={18} className={styles.cfIcon} />
                <textarea
                  rows={2}
                  value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Enter notes (optional)"
                  className={styles.cfInput}
                />
              </div>
            </div>

            <div className={styles.cfActions}>
              <button
                type="button"
                onClick={() => (initialCreate ? onClose() : setShowCreate(false))}
                className={styles.pmCancelBtn}
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button type="submit" disabled={creating} className={styles.pmCompleteBtn}>
                <Save size={18} />
                {creating ? '...' : 'Save Customer'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CustomerSelectorModal;
